import { API_URLS, getApiUrl } from '../Utils/apiURLConfiguration';
import { successfulAuthCallback } from '../Utils/authUtils';
import GWRApiClient from './GWRApiClient';

/**
 * Concrete implementation for the Manage Booking endpoints
 *
 * @class ManageBookingApiClient
 * @extends {GWRApiClient}
 */
export default class ManageBookingApiClient extends GWRApiClient {
  constructor(version = '', token = '') {
    let apiVersion = version !== '' ? version : `v1`;
    const urlAux = getApiUrl(API_URLS.REACT_APP_MANAGE_BOOKING_ENDPOINT, apiVersion);
    super(urlAux, token);
  }

  /**
   * function returns the preview cancellation information
   *
   * @function
   * @param { reservationId: string}
   */
  async previewCancel(reservationId) {
    const urlRequest = `${this.url}/reservation/${reservationId}/cancellation/preview`;
    return await super.getApiCall(urlRequest);
  }

  /**
   * function returns the add guest modal information
   *
   * @function
   * @param { reservationId: string, requestObj: {kidsAges: [number]}}
   */
  async getAddGuestsModalInformation(reservationId, requestObj) {
    const adultsString = requestObj?.adults ? 'numberOfAdults=' + requestObj.adults : '';
    let kidsAgesString = '';
    requestObj?.kidsAges &&
      requestObj.kidsAges.forEach(kid => {
        kidsAgesString += '&kidsAges=' + kid;
      });
    if (adultsString === '' && kidsAgesString !== '') kidsAgesString = kidsAgesString.substring(1);
    const hasQueryData = adultsString !== '' || kidsAgesString !== '' ? '?' : '';
    const urlRequest =
      `${this.url}/reservation/${reservationId}/guests/add/preview` + hasQueryData + adultsString + kidsAgesString;
    return await super.getApiCall(urlRequest);
  }

  /**
   * @function
   * Get all reservations for the current logged in user
   */
  async getAllReservationsForCurrentUser() {
    let request = await super.getApiCallAuth(`${this.url}/reservations`);

    return request;
  }

  /*
   * function that returns the comparison between the old reservation with the new changed one
   *
   * @function
   * @param { reservationId: string , requestObj: { arrival: string, departure: string, offercode: string }
   */
  async getComparisonChanges(reservationId, requestObj) {
    const urlRequest = `${this.url}/reservation/${reservationId}/change-dates/change`;
    return await super.getApiCallAuth(urlRequest, requestObj);
  }

  /*
   * function returns the information for the reservation
   *
   * @function
   * @param { reservationId: string }
   */
  async manageBooking(reservationId, requestObj) {
    return await super.getApiCall(`${this.url}/reservations/${reservationId}`, requestObj);
  }

  /*
   * function returns the addOns array for the reservation
   *
   * @function
   * @param {string} reservationId
   */
  async getReservationAddOnsById(reservationId) {
    const urlRequest = `${this.url}/reservation/${reservationId}/add-ons/availability`;
    return await super.getApiCall(urlRequest);
  }

  /*
   * function returns the information for the reservation, after posting packages to update
   *
   * @function
   * @param { reservationId: string , requestObj: { packages: [ packageCode: string, availabilityKey: string, quantity: number] }
   */
  async manageBookingUpdatePackages(reservationId, requestObj) {
    const urlRequest = `${this.url}/reservation/${reservationId}/add-ons`;
    return await super.postApiCallAuth(urlRequest, requestObj);
  }

  /*
   * function returns the add guest modal information after saving the new guests
   *
   * @function
   * @param { reservationId: string, requestObj: {numberOfAdults: number, kidsAges: [number]}}
   */
  async updateAddGuestsModal(reservationId, requestObj) {
    if (requestObj.numberOfAdults === 0) delete requestObj.numberOfAdults;
    const urlRequest = `${this.url}/reservation/${reservationId}/guests/add`;
    return await super.postApiCall(urlRequest, requestObj);
  }

  /**
   * Function to cancel a reservation given a reservationId
   *
   * @param {string} reservationId
   * @param { reason: string, comment: string } requestObj
   * @returns
   */
  async cancelReservation(reservationId, requestObj) {
    const urlRequest = `${this.url}/reservation/${reservationId}/cancellation`;
    return await super.postApiCall(urlRequest, requestObj);
  }

  /*
   * function to return the hold preview information
   *
   * @function
   * @param { reservationId: string }
   */
  async getHoldPreview(reservationId) {
    const urlRequest = `${this.url}/reservation/${reservationId}/hold/preview`;
    return await super.getApiCall(urlRequest);
  }

  /**
   * @function
   * Get reservations by Reservation Id and Reservation Key
   * @param {number} reservationId
   * @param {string} reservationKey
   */
  async loginReservationsByIdAndReservationKey(reservationId, reservationKey) {
    const urlRequest = `${this.url}/login-reservation?ReservationId=${reservationId}&ReservationKey=${reservationKey}`;
    const request = await super.postApiCall(urlRequest);
    return request;
  }

  /**
   * @function
   * Get reservations by Reservation Id and Reservation Key, Tioga's version
   * @param {number} reservationId
   * @param {string} reservationKey
   */
  async loginReservationByIdAndLastName(reservationId, lastName) {
    const urlRequest = `${this.url}/login-reservation?ReservationId=${reservationId}&LastName=${lastName}`;
    let request = null;
    if (super.shouldUseAuthenticatedCalls()) {
      request = await super.postApiCallAuth(urlRequest);
    } else {
      request = await super.postApiCall(urlRequest);
    }
    successfulAuthCallback(request);
    return request;
  }

  async getPoliciesForChangeDates(reservationId) {
    const urlRequest = `${this.url}/reservation/${reservationId}/change-dates/preview`;
    return await super.getApiCall(urlRequest);
  }

  /**
   * @function
   * Get folio data
   * @param {reservationId: string}
   */
  async getFolio(reservationId) {
    const urlRequest = `${this.url}/reservation/${reservationId}/folio`;
    let result = await super.getApiCall(urlRequest);

    return result;
  }

  /**
   * @function
   * Get dining credits data
   * @param {reservationId: string}
   */
  async getDiningCredits(reservationId) {
    const urlRequest = `${this.url}/reservation/${reservationId}/dining-credits`;
    let result = await super.getApiCall(urlRequest);

    return result;
  }

  /**
   * Get checking information
   * @param {reservationId: string} reservationId
   * @returns
   */
  async getReservationCheckIn(reservationId) {
    const urlRequest = `${this.url}/reservation/${reservationId}/check-in`;
    return await super.getApiCall(urlRequest);
  }
}
